/**
 * Local session and read state.
 *
 * Identity is the account: the server issues a bearer token on login, and the
 * only thing this device keeps is that token. It is sent as a header on HTTP
 * and in the `hello` frame on the socket; the server holds a hash and decides
 * whether it is still valid. Who the token belongs to comes from the server
 * (`/api/auth/me` or `welcome`), never from local storage.
 *
 * Every access is guarded: Safari private browsing and blocked site data both
 * throw on `localStorage`, and losing a session is not worth a blank screen.
 */

const TOKEN_KEY = "chatforge:token"
const READ_KEY = "chatforge:lastRead"

/** Retired keys from the anonymous protocol; cleared so nothing stale lingers. */
const LEGACY_KEYS = ["chatforge:name", "chatforge:userId"]

function read(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Preferences are a convenience; failing to persist one is not an error
    // worth surfacing to the person typing.
  }
}

function remove(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    // Nothing to do — the sign-out still proceeds in memory.
  }
}

/** Session tokens are base64url; anything else is not one the server issued. */
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{16,128}$/

export function storedToken(): string | null {
  for (const key of LEGACY_KEYS) remove(key)

  const token = read(TOKEN_KEY)
  return token && TOKEN_PATTERN.test(token) ? token : null
}

export function storeToken(token: string): void {
  write(TOKEN_KEY, token)
}

export function clearToken(): void {
  remove(TOKEN_KEY)
}

// --- Read state ------------------------------------------------------------

type ReadState = Record<string, number>

/**
 * When each room was last read, as a server timestamp. The server does not
 * track read state yet, so this is necessarily per-device.
 */
export function loadReadState(): ReadState {
  const raw = read(READ_KEY)
  if (!raw) return {}

  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== "object" || parsed === null) return {}

    // Drop anything that is not a room-id to timestamp pair.
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        (entry): entry is [string, number] => typeof entry[1] === "number"
      )
    )
  } catch {
    return {}
  }
}

export function saveReadState(state: ReadState): void {
  write(READ_KEY, JSON.stringify(state))
}
